import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import jsPDF from 'jspdf';

const InvoiceListV = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction sécurisée pour parser les dates
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      // Vérifier si la date est valide
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.warn('❌ Date invalide:', dateString, error);
      return null;
    }
  };

  // Fonction sécurisée pour formater l'affichage des dates
  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return "Date invalide";
    
    try {
      return date.toLocaleDateString('fr-FR');
    } catch (error) {
      return "Date invalide";
    }
  };

  // Fonction pour obtenir une date valide depuis une facture
  const getInvoiceDate = (invoice) => {
    // ✅ CORRECTION: Essayer plusieurs champs de date possibles
    return invoice.date || invoice.createdAt || invoice.updatedAt;
  };

  // Fonction pour comparer les dates (sans l'heure)
  const isDateBetween = (dateToCheck, start, end) => {
    const checkDate = parseDate(dateToCheck);
    const startDateObj = parseDate(start);
    const endDateObj = parseDate(end);

    if (!checkDate || !startDateObj || !endDateObj) return false;

    // Réinitialiser l'heure pour comparer seulement les dates
    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
    const startDateOnly = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());
    const endDateOnly = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), endDateObj.getDate());

    return checkDateOnly >= startDateOnly && checkDateOnly <= endDateOnly;
  };

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        console.log('🔄 Chargement des factures et fournisseurs...');
        
        // Charger les factures
        const invoicesRes = await axios.get('/api/InvoicesV/getInvoices');
        const invoicesData = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
        
        console.log(`✅ ${invoicesData.length} factures chargées`);

        // Debug: vérifier les dates avec la nouvelle fonction
        invoicesData.forEach((inv, index) => {
          const invoiceDate = getInvoiceDate(inv);
          const date = parseDate(invoiceDate);
          if (!date) {
            console.warn(`⚠️ Facture ${index} date invalide:`, invoiceDate);
            console.warn('📋 Données complètes de la facture:', inv);
          } else {
            console.log(`✅ Facture ${index} date valide:`, formatDate(invoiceDate));
          }
        });

        // Extraire les fournisseurs UNIQUES des factures
        const supplierMap = new Map();
        
        invoicesData.forEach(inv => {
          const supplier = inv.supplierV;
          if (supplier && supplier._id) {
            supplierMap.set(supplier._id, {
              _id: supplier._id,
              companyName: supplier.companyName || `Fournisseur-${String(supplier._id).slice(-6)}`
            });
          }
        });

        const extractedSuppliers = Array.from(supplierMap.values());
        console.log(`📋 ${extractedSuppliers.length} fournisseurs extraits des factures`);

        setInvoices(invoicesData);
        setFilteredInvoices(invoicesData);
        setSuppliers(extractedSuppliers);

      } catch (err) {
        console.error('❌ Erreur chargement données:', err);
        setError('Erreur lors du chargement des factures');
        setInvoices([]);
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fonction pour obtenir le nom du fournisseur
  const getSupplierName = (supplierData) => {
    if (!supplierData) return "N/A";
    
    if (typeof supplierData === 'object' && supplierData.companyName) {
      return supplierData.companyName;
    }
    
    const supplier = suppliers.find(s => s._id === supplierData);
    return supplier ? supplier.companyName : `Fournisseur-${String(supplierData).slice(-6)}`;
  };

  // Fonction pour obtenir l'ID du fournisseur (pour le filtrage)
  const getSupplierId = (supplierData) => {
    if (!supplierData) return null;
    
    if (typeof supplierData === 'object' && supplierData._id) {
      return supplierData._id;
    }
    
    return supplierData;
  };

  // Filtrer les factures par fournisseur et plage de dates
  useEffect(() => {
    let temp = [...invoices];

    // Filtre par fournisseur
    if (selectedSupplier) {
      temp = temp.filter(inv => {
        const supplierId = getSupplierId(inv.supplierV);
        return supplierId === selectedSupplier;
      });
    }

    // Filtre par plage de dates
    if (startDate && endDate) {
      temp = temp.filter(inv => {
        try {
          // Utiliser la fonction getInvoiceDate pour obtenir une date valide
          const invoiceDate = getInvoiceDate(inv);
          return isDateBetween(invoiceDate, startDate, endDate);
        } catch (error) {
          console.warn('❌ Erreur filtrage date pour facture:', inv._id, error);
          return false;
        }
      });
    } else if (startDate) {
      // Si seulement la date de début est sélectionnée
      temp = temp.filter(inv => {
        try {
          const invoiceDate = getInvoiceDate(inv);
          const invoiceDateObj = parseDate(invoiceDate);
          const startDateObj = parseDate(startDate);
          if (!invoiceDateObj || !startDateObj) return false;

          const invoiceDateOnly = new Date(invoiceDateObj.getFullYear(), invoiceDateObj.getMonth(), invoiceDateObj.getDate());
          const startDateOnly = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());

          return invoiceDateOnly >= startDateOnly;
        } catch (error) {
          return false;
        }
      });
    } else if (endDate) {
      // Si seulement la date de fin est sélectionnée
      temp = temp.filter(inv => {
        try {
          const invoiceDate = getInvoiceDate(inv);
          const invoiceDateObj = parseDate(invoiceDate);
          const endDateObj = parseDate(endDate);
          if (!invoiceDateObj || !endDateObj) return false;

          const invoiceDateOnly = new Date(invoiceDateObj.getFullYear(), invoiceDateObj.getMonth(), invoiceDateObj.getDate());
          const endDateOnly = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), endDateObj.getDate());

          return invoiceDateOnly <= endDateOnly;
        } catch (error) {
          return false;
        }
      });
    }

    setFilteredInvoices(temp);
  }, [selectedSupplier, startDate, endDate, invoices]);

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSelectedSupplier('');
    setStartDate('');
    setEndDate('');
  };

  // Générer PDF
  const handleViewInvoice = (invoice) => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("FACTURE", 105, 20, { align: "center" });

      // Utiliser la fonction getInvoiceDate pour obtenir une date valide
      const invoiceDate = getInvoiceDate(invoice);
      const dateText = formatDate(invoiceDate);
      const supplierText = getSupplierName(invoice.supplierV);

      doc.setFontSize(12);
      doc.text(`Date: ${dateText}`, 14, 30);
      doc.text(`Fournisseur: ${supplierText}`, 14, 38);

      let y = 50;
      doc.text("N°", 14, y);
      doc.text("Produit", 24, y);
      doc.text("Sous-produit", 80, y);
      doc.text("Quantité", 130, y);
      doc.text("Prix", 150, y);
      doc.text("Total", 175, y);
      y += 6;
      doc.setLineWidth(0.1);
      doc.line(14, y, 200, y);
      y += 4;

      invoice.items?.forEach((item, idx) => {
        const productName = item.productName || "-";
        const subProductName = item.subProductName || "-";
        const quantity = item.quantity || 0;
        const price = (item.price || 0).toFixed(3);
        const total = (quantity * (item.price || 0)).toFixed(3);

        doc.text(`${idx + 1}`, 14, y);
        doc.text(productName, 24, y);
        doc.text(subProductName, 80, y);
        doc.text(`${quantity} ${item.unit || ''}`, 130, y);
        doc.text(price, 150, y);
        doc.text(total, 175, y);

        y += 8;
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
      });

      const grandTotal = invoice.items?.reduce((acc, it) => 
        acc + ((it.price || 0) * (it.quantity || 0)), 0).toFixed(3) || "0.000";
      
      y += 4;
      doc.setFontSize(14);
      doc.text(`Total Général: ${grandTotal} DT`, 150, y);

      doc.save(`Facture_${invoice._id}.pdf`);
    } catch (error) {
      console.error('❌ Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">Chargement des factures...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Liste des factures</h2>

      <Form className="mb-4">
        <div className="row g-3">
          <div className="col-md-4">
            <Form.Label>Filtrer par fournisseur</Form.Label>
            <Form.Select
              value={selectedSupplier}
              onChange={e => setSelectedSupplier(e.target.value)}
            >
              <option value="">Tous les fournisseurs ({suppliers.length})</option>
              {suppliers.map(s => (
                <option key={s._id} value={s._id}>
                  {s.companyName}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Label>Date de début</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Date de fin</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              min={startDate}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <Button 
              variant="outline-secondary" 
              onClick={resetFilters}
              className="w-100"
            >
              🔄 Réinitialiser
            </Button>
          </div>
        </div>
        {(startDate || endDate) && (
          <div className="mt-2">
            <small className="text-muted">
              Filtrage par période: {startDate ? `du ${formatDate(startDate)}` : ''} {endDate ? `au ${formatDate(endDate)}` : ''}
            </small>
          </div>
        )}
      </Form>

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>Date de facture</th>
              <th>Fournisseur</th>
              <th>Articles</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map(inv => (
                <tr key={inv._id}>
                  <td>{formatDate(getInvoiceDate(inv))}</td>
                  <td>{getSupplierName(inv.supplierV)}</td>
                  <td>{inv.items?.length || 0}</td>
                  <td>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleViewInvoice(inv)}
                    >
                      📄 PDF
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted py-4">
                  {invoices.length === 0 ? 'Aucune facture disponible' : 'Aucune facture ne correspond aux filtres'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Debug info */}
      <div className="mt-4 p-3 bg-light rounded">
        <small className="text-muted">
          
          {invoices.length > 0 && (
            <div>
              Période des factures: du {formatDate(getInvoiceDate(invoices[invoices.length - 1]))} au {formatDate(getInvoiceDate(invoices[0]))}
            </div>
          )}
        </small>
      </div>
    </div>
  );
};

export default InvoiceListV;